import React from 'react';
import { Grid, HStack, Spinner, Text, Box } from '@chakra-ui/react';
import {
  Form,
  PermissionsGate,
  SubmitButton,
  Fieldset,
  ClearDraftButton,
} from 'components';
import { FormProps } from 'components/Form';
import { Title } from 'components/Meta';
import { FormSkeleton } from 'components/Skeletons';
import { CAN_EDIT_CAMPAIGNS } from 'consts/permissions';
import { withCurrentCampaignId } from 'hoc';
import { useAppSelector } from 'hooks';
import { FormLayout, FormLayoutProps } from 'layouts';
import { useHistory } from 'react-router-dom';
import { create, selectCampaign, update } from 'store/campaigns';
import { campaignShowPage } from './Campaign.Links';
import { FORM_VALIDATION_SCHEMA } from './Campaign.schema';
import { CampaignId } from './Campaign.types';
import { CampaignEndDate, CampaignName, CampaignStartDate } from './formFields';
import { useDispatch } from 'react-redux';
import { clearSelectedProducts, selectSelectedIds } from 'store/products';
import { CampaignProducts } from './formFields/Campaign.products';
import { CampaignMediaAgency } from './formFields/Campaign.mediaAgency';
import { CampaignClient } from './formFields/Campaign.client';
import { clearAdded, clearCampaignInfo } from 'store/campaigns';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useFormAutosave } from 'hooks/useFormAutosave';
import { useDeleteAutosave } from 'queries/autosave/autosave';
import { useCampaignAutosaveHydration } from './Campaign.CreateAutosaveHydration';
import { AutosaveBadge } from 'components/AutosaveBadge';

const sortIds = (ids: (number | string)[] = []): (number | string)[] =>
  [...ids].sort((a, b) => Number(a) - Number(b));

const equalSortedIds = (
  ids1: (number | string)[] = [],
  ids2: (number | string)[] = [],
) => {
  const sorted1 = sortIds(ids1);
  const sorted2 = sortIds(ids2);
  return JSON.stringify(sorted1) === JSON.stringify(sorted2);
};

/**
 *
 * Campaign Form Content for Campaign Form
 *
 * Writes live form state back to the autosave draft, mirroring `products`
 * from Redux. Complements useCampaignAutosaveHydration, which restores the
 * draft into Redux on mount — same key ('campaign/create'), opposite
 * direction.
 */
const CampaignFormContent = ({
  editMode,
  onSavingChange,
  dirtySinceLastSave,
  setDirtySinceLastSave,
}: {
  editMode: boolean;
  onSavingChange?: (isSaving: boolean) => void;
  dirtySinceLastSave: boolean;
  setDirtySinceLastSave: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    setValue,
    watch,
    reset,
    formState: { isDirty },
  } = useFormContext();

  const formData = watch() as Record<string, any>;
  const selectedProductIds = useAppSelector(selectSelectedIds);

  React.useEffect(() => {
    if (isDirty) setDirtySinceLastSave(true);
  }, [isDirty, setDirtySinceLastSave]);

  React.useEffect(() => {
    const formProductIds = sortIds(
      (formData?.products as (number | string)[]) || [],
    );
    const reduxProductIds = sortIds(selectedProductIds || []);

    if (!equalSortedIds(formProductIds, reduxProductIds)) {
      setValue('products', selectedProductIds, {
        shouldValidate: true,
        shouldDirty: false,
      });
      setDirtySinceLastSave(true);
    }
  }, [formData?.products, selectedProductIds, setValue, setDirtySinceLastSave]);

  const productsSynced = React.useMemo(() => {
    const formProductIds = sortIds(
      (formData?.products as (number | string)[]) || [],
    );
    const reduxProductIds = sortIds(selectedProductIds || []);
    return equalSortedIds(formProductIds, reduxProductIds);
  }, [formData?.products, selectedProductIds]);

  const dataToSave = React.useMemo(
    () => ({
      ...formData,
      products: selectedProductIds,
      timestamp: new Date().toISOString(),
    }),
    [formData, selectedProductIds],
  );

  const handleAutosaveSaved = React.useCallback(
    (savedData: Record<string, any>) => {
      reset(savedData);
      setDirtySinceLastSave(false);
    },
    [reset, setDirtySinceLastSave],
  );

  const { isSaving } = useFormAutosave({
    key: 'campaign/create',
    data: dataToSave,
    enabled: !editMode && productsSynced,
    shouldSave: dirtySinceLastSave,
    delay: 1000,
    onSaved: handleAutosaveSaved,
  });

  React.useEffect(() => {
    if (onSavingChange) {
      onSavingChange(isSaving);
    }
  }, [isSaving, onSavingChange]);

  return (
    <Grid gap={2} templateColumns='repeat(2,1fr)'>
      <Fieldset gridColumn='span 2'>
        <CampaignName gridColumn={{ base: 'span 6', md: 'span 6' }} />
        <CampaignMediaAgency
          gridColumn={{ base: 'span 3', md: 'span 3' }}
          isDisabled={editMode}
        />
        <CampaignClient
          gridColumn={{ base: 'span 3', md: 'span 3' }}
          isDisabled={editMode}
        />
        <CampaignStartDate gridColumn={{ base: 'span 6', md: 'span 3' }} />
        <CampaignEndDate gridColumn={{ base: 'span 6', md: 'span 3' }} />
        {!editMode && <CampaignProducts gridColumn='span 12' />}
      </Fieldset>
    </Grid>
  );
};

interface CampaignFormProps extends FormProps, FormLayoutProps {
  redirectOnSuccess?: boolean;
  editMode?: boolean;
  budgetPerLineitem?: boolean;
  forceAutosaveOnInit?: boolean;
  defaultValuesSchema?: Record<string, any>;
  hasAutosaveDraft?: boolean;
  onDraftDiscarded?: () => void;
}

const CampaignForm = ({
  heading,
  defaultValues,
  onSubmit,
  template,
  redirectOnSuccess,
  editMode,
  forceAutosaveOnInit = false,
  defaultValuesSchema,
  hasAutosaveDraft,
  onDraftDiscarded,
  ...props
}: CampaignFormProps): JSX.Element => {
  const { t } = useTranslation();
  const deleteAutosaveMutation = useDeleteAutosave();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = React.useState(false);
  const [dirtySinceLastSave, setDirtySinceLastSave] = React.useState(
    forceAutosaveOnInit,
  );

  async function handleSubmit(campaign: any): Promise<void> {
    if (!editMode) {
      await deleteAutosaveMutation.mutateAsync('campaign/create');
    }
    if (campaign.id && redirectOnSuccess) {
      dispatch(clearAdded());
      dispatch(clearCampaignInfo());
      dispatch(clearSelectedProducts());
      history.push(campaignShowPage(campaign.id));
    }
    if (typeof onSubmit === 'function') onSubmit(campaign);
  }

  return (
    <Form
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      schema={FORM_VALIDATION_SCHEMA}
      permissionsToEdit={[CAN_EDIT_CAMPAIGNS]}
      {...props}
    >
      <FormLayout template={template}>
        <FormLayout.Heading>
          <HStack spacing={2}>
            <Box>{heading}</Box>
            {isSaving && (
              <HStack spacing={2}>
                <Spinner size='xs' />
                <Text fontSize='sm' color='gray.500'>
                  {t('form.savingDraft', 'Zapisywanie szkicu...')}
                </Text>
              </HStack>
            )}
          </HStack>
        </FormLayout.Heading>
        <PermissionsGate requiredPermissions={[CAN_EDIT_CAMPAIGNS]}>
          <FormLayout.Actions>
            <SubmitButton>
              {editMode
                ? t('campaign.saveChanges', 'Zapisz zmiany')
                : t('campaign.createCampaign', 'Stwórz kampanię')}
            </SubmitButton>
            <ClearDraftButton
              defaultValuesSchema={defaultValuesSchema}
              isDraftAvailable={hasAutosaveDraft}
              preserveFields={['products']}
              onDraftDiscarded={() => {
                setDirtySinceLastSave(false);
                if (onDraftDiscarded) onDraftDiscarded();
              }}
            />
          </FormLayout.Actions>
        </PermissionsGate>
        <FormLayout.Body>
          <CampaignFormContent
            editMode={editMode || false}
            onSavingChange={setIsSaving}
            dirtySinceLastSave={dirtySinceLastSave}
            setDirtySinceLastSave={setDirtySinceLastSave}
          />
        </FormLayout.Body>
      </FormLayout>
    </Form>
  );
};

export const CreateCampaignForm = ({
  ...props
}: FormLayoutProps): JSX.Element => {
  const { t } = useTranslation();
  const DEFAULT_VALUES = FORM_VALIDATION_SCHEMA.getDefault();
  const deleteAutosaveMutation = useDeleteAutosave();
  const campaignInfo = useAppSelector(state => state.campaigns.campaignInfo);
  const {
    autosaveData,
    isLoading,
    selectedProductIds,
  } = useCampaignAutosaveHydration({ logScope: 'CampaignCreateForm' });

  /** Only the very first load should block rendering. Discarding the draft
   * deletes its query cache entry, which makes the still-mounted query
   * observer refetch and flicker `isLoading` back to true — gating on that
   * every time would unmount/remount CampaignForm and reset its autosave
   * state, immediately re-triggering a save of the just-cleared form.
   */
  const hasLoadedOnceRef = React.useRef(false);
  if (!isLoading) hasLoadedOnceRef.current = true;

  const autosaveProductIds = React.useMemo(
    () => sortIds((autosaveData?.products as (number | string)[]) || []),
    [autosaveData?.products],
  );

  const currentSelectedProductIds = React.useMemo(
    () => sortIds(selectedProductIds || []),
    [selectedProductIds],
  );

  const forceAutosaveOnInit = React.useMemo(
    () => !equalSortedIds(autosaveProductIds, currentSelectedProductIds),
    [autosaveProductIds, currentSelectedProductIds],
  );

  const hasAutosaveDraft = React.useMemo(() => {
    if (!autosaveData) return false;
    const { name, client, mediaAgency } = autosaveData;
    return Boolean(
      (name && String(name).trim().length > 0) || client || mediaAgency,
    );
  }, [autosaveData]);

  const values = React.useMemo(
    () => ({
      ...DEFAULT_VALUES,
      ...(campaignInfo || {}),
      ...(autosaveData || {}),
      products: selectedProductIds || [],
    }),
    [campaignInfo, autosaveData, selectedProductIds],
  );

  if (!hasLoadedOnceRef.current) return <FormSkeleton />;
  return (
    <>
      {autosaveData && <AutosaveBadge timestamp={autosaveData.timestamp} />}
      <CampaignForm
        heading={t('campaign.createCampaign', 'Stwórz kampanię')}
        messageOnSuccess={t(
          'campaign.createCampaignSuccess',
          'Stworzono nową kampanię',
        )}
        dispatchMethod={create}
        defaultValues={values}
        defaultValuesSchema={DEFAULT_VALUES}
        redirectOnSuccess={true}
        forceAutosaveOnInit={forceAutosaveOnInit}
        hasAutosaveDraft={hasAutosaveDraft}
        onDraftDiscarded={() =>
          deleteAutosaveMutation.mutate('campaign/create')
        }
        {...props}
      />
    </>
  );
};

export const EditCampaignForm = ({
  campaignId,
  ...props
}: FormLayoutProps & CampaignId): JSX.Element => {
  const { t } = useTranslation();
  const campaignValues = useAppSelector(state =>
    selectCampaign(state, campaignId),
  );

  if (!campaignValues) return <FormSkeleton />;

  return (
    <>
      <Title
        name={t<string>(
          'campaign.editCampaignWithName',
          'Edytuj kampanię {{name}}',
          {
            name: campaignValues.name,
          },
        )}
      />
      <CampaignForm
        heading={t('campaign.editCampaign', 'Edytuj kampanię')}
        messageOnSuccess={t(
          'campaign.editCampaignSuccess',
          'Wszystkie zmiany zostały zapisane',
        )}
        dispatchMethod={update}
        defaultValues={campaignValues}
        editMode={true}
        redirectOnSuccess={true}
        {...props}
      />
    </>
  );
};

export const EditCurrentCampaignForm = withCurrentCampaignId(EditCampaignForm);
